import { PaperclipIcon, SparkleIcon } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import { useImageDropZone } from "../../hooks/useImageDropZone";
import { MAX_REFERENCE_IMAGES } from "../../lib/image-constants";
import { readFileInputChange } from "../../lib/image-input";
import { isRegenerateDisabled } from "../../lib/parameter-panel-helpers";
import { Button } from "../Button";

/** The slice of RefinePageState this bar reads. Narrower than the page state so
 *  the component can be exercised without a full page mock. */
export interface PromptBarState {
  isPromptOpen: boolean;
  openPrompt: () => void;
  closePrompt: () => void;
  promptText: string;
  handlePromptChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  referenceImages: string[];
  /** Attaches a picked image once it has been prepared. */
  attachReferenceImage: (file: File) => void;
  /** An attached image is still being prepared. */
  isAttachingImage: boolean;
  isRegenerating: boolean;
  isLoading: boolean;
  isExtractingParameters: boolean;
  isResyncingParameters: boolean;
  isParametersDirty: boolean;
  promptDirty: boolean;
  handleRegenerateImages: () => void;
}

/** The `Prompt` control in the bottom action row. Collapsed it is a plain pill;
 *  opened it expands in place to fill the rest of the row, keeping the closed
 *  pill's height so the image stage does not reflow.
 *
 *  Submit lives inside the field (left of the attach icon) so it stays clear of
 *  the Sentry feedback trigger in the viewport corner. Visible even when the
 *  parameter drawer is collapsed. */
export function RefinePromptBar({ page }: { page: PromptBarState }) {
  const {
    isPromptOpen,
    openPrompt,
    closePrompt,
    promptText,
    handlePromptChange,
    referenceImages,
  } = page;

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBusy =
    page.isRegenerating || page.isLoading || page.isExtractingParameters;
  // Parameters and prompt are mutually exclusive: once a parameter is edited the
  // prompt is locked out until that edit is submitted or reverted.
  const isLockedByParameters = page.isParametersDirty && !page.promptDirty;
  const isDisabled = isBusy || isLockedByParameters;
  const isAttachDisabled =
    isDisabled || referenceImages.length >= MAX_REFERENCE_IMAGES;

  useEffect(() => {
    if (!isPromptOpen) return;
    inputRef.current?.focus();
  }, [isPromptOpen]);

  const processing = {
    isRegenerating: page.isRegenerating,
    isInitialLoading: page.isLoading,
    isExtractingParameters: page.isExtractingParameters,
    isResyncingParameters: page.isResyncingParameters,
    isAttachingImage: page.isAttachingImage,
    isExpandingGroup: false,
    isExpandingElements: false,
  };
  const hasChanges =
    page.isParametersDirty || page.promptDirty || referenceImages.length > 0;
  const isSubmitDisabled = isRegenerateDisabled(processing, hasChanges);

  const dropZoneProps = useImageDropZone({
    onFile: page.attachReferenceImage,
    disabled: isAttachDisabled,
  });

  if (!isPromptOpen) {
    return (
      <Button
        variant="toolbar"
        onClick={openPrompt}
        disabled={isDisabled}
        aria-expanded={false}
        className="shrink-0"
      >
        Prompt
        <SparkleIcon size={16} aria-hidden />
      </Button>
    );
  }

  return (
    <div {...dropZoneProps} className="flex min-w-0 flex-1 items-center">
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-[4px] border border-[var(--stroke-field)] bg-[var(--bg-field)] px-2 py-1">
        <button
          type="button"
          onClick={closePrompt}
          aria-expanded
          className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium leading-5 text-white"
        >
          Prompt
          <SparkleIcon size={16} aria-hidden />
        </button>

        <textarea
          ref={inputRef}
          rows={1}
          value={promptText}
          onChange={handlePromptChange}
          onKeyDown={(e) => {
            if (e.key === "Escape") closePrompt();
          }}
          disabled={isDisabled}
          aria-label="Refinement prompt"
          className="h-5 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent text-[13px] font-normal leading-5 text-[var(--text-primary)] outline-none placeholder:text-[var(--text-secondary)] disabled:cursor-not-allowed disabled:opacity-60"
        />

        <div className="flex h-5 shrink-0 items-center gap-2">
          <div aria-hidden className="h-3 w-px bg-[var(--stroke-field)]" />
          <button
            type="button"
            onClick={page.handleRegenerateImages}
            disabled={isSubmitDisabled}
            className="inline-flex h-5 shrink-0 items-center rounded-[3px] border border-transparent px-2 text-[13px] font-medium text-white transition-colors hover:border-[var(--stroke-field)] hover:bg-white/10 focus-visible:border-[var(--stroke-field)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent"
          >
            Refine Further
          </button>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => readFileInputChange(e, page.attachReferenceImage)}
          disabled={isAttachDisabled}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isAttachDisabled}
          aria-label="Attach reference image"
          className="shrink-0 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PaperclipIcon size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
